import { useEffect, useRef, useState } from 'react'
import { findPattern, useOutsideClose } from './findScope'
import type { FindHit } from './findScope'

// 찾기/바꾸기 팝업 — 태그 편집·항목 편집이 같이 쓴다. 범위(이 시트·모든 시트·선택 영역)와 열을 고른다.
// [찾기]·[바꾸기] 탭으로 나눈다 — 찾기 탭에는 바꾸는 단추가 없어 찾으려다 잘못 바꾸지 않는다 (Ctrl+F 찾기, Ctrl+H 바꾸기)
//  · [다음]·[이전] (Enter · Shift+Enter) — 찾은 칸으로 표 커서를 옮긴다 (다른 시트면 탭도). 닫으면 커서는 그 칸에 남는다
//  · [바꾸기] — 지금 찾은 칸만 바꾸고 다음 칸으로, [모두 바꾸기] — 범위 안 전부
// 무엇을 찾고 어떻게 바꿀지는 쓰는 쪽이 정한다 (search·onReplace·onGo). 대소문자는 가리지 않는다.
// 찾은 칸으로 커서를 옮기면 표가 문서 전체의 키 입력을 가져가므로, 팝업 안에서 친 키·붙여넣기는 표에 가지 않게 먼저 막는다.

interface FindChoice { key: string; label: string }
export type FindMode = 'find' | 'replace'

export default function FindReplace({ mode, onMode, scopes, columns, initialScope, focusKey, activeSheet, search, onReplace, onGo, onClose }: {
  mode: FindMode
  onMode: (mode: FindMode) => void
  scopes: FindChoice[]
  columns: FindChoice[] // 첫 항목 = 모든 열
  initialScope?: string
  focusKey: number // 바뀔 때마다 [찾을 내용] 칸으로 (Ctrl+F 다시 누름)
  activeSheet: string | null // 처음 [다음] 은 지금 시트의 칸부터
  search: (pattern: RegExp, scope: string, column: string) => FindHit[]
  onReplace: (pattern: RegExp, replacement: string, scope: string, column: string, only?: FindHit) => void
  onGo: (hit: FindHit) => void
  onClose: () => void
}) {
  const ref = useOutsideClose(onClose)
  const [find, setFind] = useState('')
  const [replace, setReplace] = useState('')
  const [scope, setScope] = useState(initialScope ?? scopes[0].key)
  const [column, setColumn] = useState(columns[0].key)
  // 지금 찾은 칸 — 찾을 내용·범위·열이 바뀌면 처음부터 (q 가 다르면 없는 것으로)
  const [cursor, setCursor] = useState<{ q: string; hit: FindHit } | null>(null)
  const findRef = useRef<HTMLInputElement>(null)
  // 표가 커서를 놓는 것과 같은 때 그려지므로 한 박자 뒤에 잡는다
  useEffect(() => {
    const id = requestAnimationFrame(() => { findRef.current?.focus(); findRef.current?.select() })
    return () => cancelAnimationFrame(id)
  }, [focusKey])

  const validScope = scopes.some(s => s.key === scope) ? scope : scopes[0].key
  const pattern = findPattern(find)
  const hits = pattern ? search(pattern, validScope, column) : []
  const total = hits.reduce((n, h) => n + h.count, 0)
  const q = `${find}
${validScope}
${column}`
  const same = (a: FindHit, b: FindHit) => a.sheet === b.sheet && a.row === b.row && a.field === b.field
  const index = cursor && cursor.q === q ? hits.findIndex(h => same(h, cursor.hit)) : -1
  const current = index >= 0 ? hits[index] : null

  const moveTo = (hit: FindHit) => { setCursor({ q, hit }); onGo(hit) }
  const go = (i: number) => { if (hits.length) moveTo(hits[(i + hits.length) % hits.length]) }
  const step = (delta: 1 | -1) => {
    if (index >= 0) return go(index + delta)
    const first = hits.findIndex(h => h.sheet === activeSheet)
    go(first >= 0 ? (delta === 1 ? first : first - 1) : delta === 1 ? 0 : -1)
  }

  const replaceOne = () => {
    if (!pattern || mode !== 'replace') return
    if (!current) return step(1) // 아직 찾은 칸이 없으면 먼저 찾기
    // 그 칸만 바꾸고 다음 칸으로 (바꾼 칸은 목록에서 빠진다)
    const next = hits.length > 1 ? hits[(index + 1) % hits.length] : null
    onReplace(pattern, replace, validScope, column, current)
    if (next) moveTo(next)
    else setCursor(null)
  }
  const replaceAll = () => {
    if (!pattern || total === 0 || mode !== 'replace') return
    onReplace(pattern, replace, validScope, column)
    onClose()
  }

  // 팝업 안의 키·복사·붙여넣기는 표로 보내지 않는다 (찾은 칸에 커서가 있어도) — 맨 먼저(window 캡처) 가로채 여기서 처리
  const handlers = useRef({ step, replaceOne, onClose, onMode })
  useEffect(() => { handlers.current = { step, replaceOne, onClose, onMode } })
  useEffect(() => {
    const inside = (e: Event) => !!ref.current && ref.current.contains(e.target as Node)
    const onKey = (e: KeyboardEvent) => {
      if (!inside(e)) return
      e.stopPropagation()
      if (e.key === 'Escape') { e.preventDefault(); handlers.current.onClose() }
      else if (e.key === 'Enter' && !e.isComposing) {
        e.preventDefault()
        if ((e.target as HTMLElement).dataset.role === 'replace') handlers.current.replaceOne()
        else handlers.current.step(e.shiftKey ? -1 : 1)
      } else if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key.toLowerCase() === 'f' || e.key.toLowerCase() === 'h')) {
        e.preventDefault()
        handlers.current.onMode(e.key.toLowerCase() === 'h' ? 'replace' : 'find')
        findRef.current?.focus(); findRef.current?.select()
      }
    }
    const onClip = (e: Event) => { if (inside(e)) e.stopPropagation() }
    window.addEventListener('keydown', onKey, true)
    for (const t of ['copy', 'cut', 'paste']) window.addEventListener(t, onClip, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      for (const t of ['copy', 'cut', 'paste']) window.removeEventListener(t, onClip, true)
    }
  }, [ref])

  const input = 'w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-surface'
  const btn = 'px-2 py-1.5 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40'
  return (
    // 팝업을 눌러도 표가 '바깥 클릭'으로 커서를 풀지 않게
    <div ref={ref} onMouseDown={e => e.stopPropagation()}
      className="absolute left-0 top-full mt-1 z-50 w-80 bg-surface border border-gray-200 rounded-lg shadow-lg p-3 space-y-2">
      <div className="flex border-b border-gray-200 -mt-1 mb-1">
        {([['find', '찾기', 'Ctrl+F'], ['replace', '바꾸기', 'Ctrl+H']] as const).map(([m, label, key]) => (
          <button key={m} onClick={() => { onMode(m); findRef.current?.focus() }} title={key}
            className={`px-3 py-1.5 text-xs -mb-px border-b-2 ${mode === m ? 'border-accent text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        <input ref={findRef} value={find} onChange={e => setFind(e.target.value)} placeholder="찾을 내용 (Enter 다음 · Shift+Enter 이전)"
          className={`${input} font-mono`} />
        <button onClick={() => step(-1)} disabled={!hits.length} title="이전 (Shift+Enter)" className={btn}>▲</button>
        <button onClick={() => step(1)} disabled={!hits.length} title="다음 (Enter)" className={btn}>▼</button>
      </div>
      {mode === 'replace' && (
        <input data-role="replace" value={replace} onChange={e => setReplace(e.target.value)} placeholder="바꿀 내용 (비우면 지우기, Enter 바꾸기)"
          className={`${input} font-mono`} />
      )}
      <div className="flex gap-2">
        <select value={validScope} onChange={e => setScope(e.target.value)} className={input}>
          {scopes.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={column} onChange={e => setColumn(e.target.value)} className={input}>
          {columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <span className="text-xs text-gray-500">
          {find ? (hits.length ? `${current ? `${index + 1} / ` : ''}${hits.length}칸 · ${total}곳` : '없음') : ''}
        </span>
        {mode === 'find' ? (
          <button onClick={() => step(1)} disabled={!hits.length}
            className="ml-auto bg-blue-600 text-white px-3 py-1.5 rounded-md text-xs hover:bg-accent-hover disabled:opacity-40">다음 찾기</button>
        ) : (
          // 바꿀 내용이 비었으면 찾은 글자를 지우는 것 — 단추 이름·색으로 드러낸다
          <>
            <button onClick={replaceOne} disabled={!hits.length} className={`ml-auto ${btn}`}>{replace ? '바꾸기' : '지우기'}</button>
            <button onClick={replaceAll} disabled={!find || total === 0}
              className={`text-white px-3 py-1.5 rounded-md text-xs disabled:opacity-40 ${replace ? 'bg-blue-600 hover:bg-accent-hover' : 'bg-red-600 hover:bg-red-700'}`}>
              {replace ? '모두 바꾸기' : '모두 지우기'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
